import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  CheckCircle, ArrowForward, ExpandMore,
  Email, Shield, SupportAgent, AdminPanelSettings,
  DomainVerification, Palette, Speed, Lock,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: '15 min', label: 'Setup Time' },
  { value: '₹0', label: 'Setup Fee' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '< 24hr', label: 'Support Response' },
];

const pillars = [
  {
    color: '#dc2626',
    icon: <Email sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Professional Email Identity',
    desc: 'Replace @gmail or @yahoo with your own domain. Every email you send builds brand credibility — whether you have 2 employees or 20.',
    points: [
      'user@yourcompany.com email addresses',
      'Custom login page on your domain',
      'Shared inbox & alias support',
      'No CruvzMail branding visible',
    ],
  },
  {
    color: '#1565c0',
    icon: <AdminPanelSettings sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Simple Admin, No IT Needed',
    desc: 'Add users, reset passwords, set quotas, and manage domains from a clean admin dashboard — no server access or technical expertise required.',
    points: [
      'Manage everything from one dashboard',
      'Add/remove users in seconds',
      'Set per-user storage quotas',
      'Password reset without helpdesk',
    ],
  },
  {
    color: '#059669',
    icon: <Shield sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Enterprise Security at SMB Price',
    desc: 'DKIM, SPF, DMARC, TLS 1.3, spam filtering, and 2FA. The same security stack used by large organizations — included at no extra cost.',
    points: [
      'Auto-configured SPF/DKIM/DMARC',
      'Rspamd spam & phishing filter',
      'Two-factor authentication (2FA)',
      'Encrypted in-transit (TLS 1.3)',
    ],
  },
];

const features = [
  { icon: <DomainVerification sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'Custom Domain Email', desc: 'Use your own domain. MX records take 5 minutes to set up.' },
  { icon: <Palette sx={{ fontSize: 22, color: '#1565c0' }} />, title: 'Branded Webmail', desc: 'Your logo and colours on the login page and webmail interface.' },
  { icon: <Speed sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Fast & Reliable', desc: '99.9% uptime guarantee. Email delivered in milliseconds.' },
  { icon: <Lock sx={{ fontSize: 22, color: '#059669' }} />, title: '2FA Security', desc: 'Protect accounts with two-factor authentication.' },
  { icon: <SupportAgent sx={{ fontSize: 22, color: '#d97706' }} />, title: 'Email & Chat Support', desc: 'Real support — not a chatbot. Response within 24 hours.' },
  { icon: <Email sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'Aliases & Shared Inboxes', desc: 'info@, support@, sales@ — route to any mailbox or team.' },
];

const faqs = [
  { q: 'How quickly can I get started?', a: 'Point your domain\'s MX records to CruvzMail and add your users — most small businesses are fully up and running within 15–30 minutes. We have a step-by-step setup guide, and our support team is available to help.' },
  { q: 'Do I need any technical knowledge?', a: 'No. The admin panel is designed for business owners, not IT professionals. Adding users, setting passwords, creating aliases, and managing quotas all take a few clicks. For domain DNS setup, we provide exact records to copy-paste.' },
  { q: 'What happens if I need to add more users later?', a: 'You can add mailboxes any time from the admin panel. Your billing adjusts based on active mailboxes. There\'s no lock-in — scale up or down as your team changes.' },
  { q: 'Is my email data stored in India?', a: 'Yes. CruvzMail is hosted entirely in our own Indian datacenter infrastructure. Your email data never leaves India, keeping you aligned with the DPDP Act 2023.' },
  { q: 'Can I migrate from Gmail or another provider?', a: 'Yes. We support email migration from Gmail, Outlook, Yahoo, and most IMAP providers. Our migration guide walks you through exporting and importing mail with zero permanent loss.' },
];

const SmallBusinessPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #052e16 50%, #0f172a 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(5,150,105,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Small Business" sx={{ mb: 3, bgcolor: 'rgba(5,150,105,0.15)', color: '#6ee7b7', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(5,150,105,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #6ee7b7 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Professional Email for<br />Small Businesses
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 600, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Get your team on user@yourbusiness.com in under 15 minutes — with spam protection, 2FA security, and an admin dashboard that anyone can use. No IT team needed.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#059669', boxShadow: '0 0 40px rgba(5,150,105,0.4)', '&:hover': { bgcolor: '#047857' } }}>
              Get Started Free
            </Button>
            <Button onClick={() => navigate('/pricing')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View Pricing
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#6ee7b7', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.4 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Why Choose CruvzMail</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Everything a Small Business Needs
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 500, mx: 'auto' }}>
              Enterprise-grade email — without the enterprise complexity or enterprise pricing.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff', position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FEATURES ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1 }}>
              Everything Included, No Add-ons
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>Every plan includes these features — no hidden extras.</Typography>
          </Box>
          <Grid container spacing={2.5}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fafafa', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } }}>
                  <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.6 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>Common Questions</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' }, '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#a7f3d0' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#f0fdf4' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#f0fdf4' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, bgcolor: '#0f172a', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(5,150,105,0.1) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 620, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Start with Professional Email Today
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.85 }}>
            Setup takes 15 minutes. No credit card required to start. Your data stays in India.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
              Create Your Account
            </Button>
            <Button onClick={() => navigate('/contact')} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Talk to Us
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default SmallBusinessPage;
